'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsTrack } from '@/components/analytics/hooks';

type AnalyticsEventName = 'View' | 'Input' | 'Submit' | 'Click' | 'Select';

type CommonProps = {
    checkout_id: string;
    page_path: string;
    ts: number;
    elapsed_ms?: number;
    flow_type: 'new_subscription' | 'manage_subscription';
    auth_state: 'authenticated' | 'guest';
};

type Options = {
    isManagingSubscription: boolean;
    isAuthenticated: boolean;
    showLoginForm: boolean;
    isEditingInvoiceInformation: boolean;
    selectedPricesCount: number;
    selectedPaymentMethodId: string | null;
    paymentMethodsCount: number;
    isLoadingPaymentMethods: boolean;
    hasPaymentMethodError: boolean;
    isProcessing: boolean;
    isLoggingIn: boolean;
    isFormDirty: boolean;
    isPendingCancellation?: boolean;
};

function now() {
    return Date.now();
}

function genCheckoutId() {
    return `chk_${Math.random().toString(36).slice(2, 11)}_${Date.now().toString(36)}`;
}

function getPagePath(pathname?: string | null, searchParams?: URLSearchParams | null) {
    const search = searchParams?.toString();
    return search ? `${pathname ?? ''}?${search}` : (pathname ?? '');
}

export function useCheckoutAnalytics({
    isManagingSubscription,
    isAuthenticated,
    showLoginForm,
    isEditingInvoiceInformation,
    selectedPricesCount,
    selectedPaymentMethodId,
    paymentMethodsCount,
    isLoadingPaymentMethods,
    hasPaymentMethodError,
    isProcessing,
    isLoggingIn,
    isFormDirty,
    isPendingCancellation = false,
}: Options) {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const checkoutIdRef = useRef(genCheckoutId());
    const startedAtRef = useRef<number | null>(null);

    const hasTrackedFlowViewRef = useRef(false);
    const hasTrackedEmailStartedRef = useRef(false);
    const hasTrackedInvoiceStartedRef = useRef(false);
    const lastStageRef = useRef<string | null>(null);
    const lastSelectedPaymentMethodIdRef = useRef<string | null>(null);
    const lastProcessingRef = useRef(false);
    const lastLoggingInRef = useRef(false);

    const locationKey = useMemo(() => {
        const search = searchParams?.toString();
        return search ? `${pathname}?${search}` : (pathname ?? '');
    }, [pathname, searchParams]);

    const trackEvent = useAnalyticsTrack();

    const track = useCallback(
        (
            event: AnalyticsEventName,
            {
                action,
                category = 'Checkout',
                interaction = event,
                ...props
            }: Record<string, unknown> & {
                action: string;
                category?: string;
                interaction?: AnalyticsEventName;
            }
        ) => {
            trackEvent(event, {
                category,
                action,
                interaction,
                ...props,
            });
        },
        [trackEvent]
    );

    const common = useCallback(
        (extra?: Record<string, unknown>): CommonProps & Record<string, unknown> => ({
            checkout_id: checkoutIdRef.current,
            page_path: getPagePath(pathname, searchParams),
            ts: now(),
            ...(startedAtRef.current ? { elapsed_ms: now() - startedAtRef.current } : {}),
            flow_type: isManagingSubscription ? 'manage_subscription' : 'new_subscription',
            auth_state: isAuthenticated ? 'authenticated' : 'guest',
            ...(extra ?? {}),
        }),
        [isAuthenticated, isManagingSubscription, pathname, searchParams]
    );

    const getStage = useCallback(() => {
        if (!isAuthenticated) {
            return showLoginForm ? 'login' : 'email';
        }

        return isEditingInvoiceInformation ? 'invoice_information' : 'payment_method';
    }, [isAuthenticated, isEditingInvoiceInformation, showLoginForm]);

    const getDisabledReason = useCallback(() => {
        if (isPendingCancellation) return 'pending_cancellation';
        if (selectedPricesCount === 0) return 'no_selected_prices';
        if (isManagingSubscription && !isFormDirty) return 'no_changes';
        if (isProcessing) return 'processing';
        if (isLoggingIn) return 'logging_in';
        return null;
    }, [isPendingCancellation, selectedPricesCount, isManagingSubscription, isFormDirty, isProcessing, isLoggingIn]);

    useEffect(() => {
        checkoutIdRef.current = genCheckoutId();
        startedAtRef.current = now();
        hasTrackedFlowViewRef.current = false;
        hasTrackedEmailStartedRef.current = false;
        hasTrackedInvoiceStartedRef.current = false;
        lastStageRef.current = null;
        lastSelectedPaymentMethodIdRef.current = null;
        lastProcessingRef.current = false;
        lastLoggingInRef.current = false;
    }, [locationKey]);

    useEffect(() => {
        if (hasTrackedFlowViewRef.current) return;

        hasTrackedFlowViewRef.current = true;

        track('View', {
            action: 'Flow Viewed',
            category: 'Checkout',
            ...common({
                selected_prices_count: selectedPricesCount,
            }),
        });
    }, [common, selectedPricesCount, track]);

    useEffect(() => {
        const stage = getStage();
        if (lastStageRef.current === stage) return;

        lastStageRef.current = stage;

        track('View', {
            action: `Stage Viewed - ${stage}`,
            category: 'Checkout',
            ...common({
                stage,
                selected_prices_count: selectedPricesCount,
            }),
        });

        if (stage === 'login') {
            track('View', {
                action: 'Existing Account Prompt Viewed',
                category: 'Checkout',
                ...common({
                    selected_prices_count: selectedPricesCount,
                }),
            });

            track('View', {
                action: 'Login Viewed',
                category: 'Checkout',
                ...common({
                    selected_prices_count: selectedPricesCount,
                }),
            });
        }

        if (stage === 'invoice_information' && !hasTrackedInvoiceStartedRef.current) {
            hasTrackedInvoiceStartedRef.current = true;

            track('View', {
                action: 'Invoice Started',
                category: 'Checkout',
                ...common({
                    stage: 'invoice_information',
                }),
            });
        }
    }, [common, getStage, selectedPricesCount, track]);

    useEffect(() => {
        if (!selectedPaymentMethodId) return;
        if (lastSelectedPaymentMethodIdRef.current === selectedPaymentMethodId) return;

        lastSelectedPaymentMethodIdRef.current = selectedPaymentMethodId;

        track('Select', {
            action: 'Payment Method Selected',
            category: 'Checkout',
            ...common({
                stage: getStage(),
                has_saved_payment_methods: paymentMethodsCount > 0,
                payment_methods_count: paymentMethodsCount,
                selected_payment_method_id: selectedPaymentMethodId,
            }),
        });
    }, [common, getStage, paymentMethodsCount, selectedPaymentMethodId, track]);

    useEffect(() => {
        const wasLoggingIn = lastLoggingInRef.current;

        if (!wasLoggingIn && isLoggingIn) {
            track('Submit', {
                action: 'Login Submit Started',
                category: 'Checkout',
                ...common({
                    submit_type: 'login',
                    stage: getStage(),
                }),
            });
        }

        if (wasLoggingIn && !isLoggingIn) {
            track('Submit', {
                action: 'Login Submit Finished',
                category: 'Checkout',
                ...common({
                    submit_type: 'login',
                    stage: getStage(),
                    success: !showLoginForm,
                }),
            });
        }

        lastLoggingInRef.current = isLoggingIn;
    }, [common, getStage, isLoggingIn, showLoginForm, track]);

    useEffect(() => {
        const wasProcessing = lastProcessingRef.current;

        if (!wasProcessing && isProcessing) {
            track('Submit', {
                action: isManagingSubscription ? 'Update Subscription Submit Started' : 'Complete Order Submit Started',
                category: 'Checkout',
                ...common({
                    submit_type: isManagingSubscription ? 'update_subscription' : 'complete_order',
                    stage: getStage(),
                    selected_prices_count: selectedPricesCount,
                    has_selected_payment_method: !!selectedPaymentMethodId,
                }),
            });
        }

        if (wasProcessing && !isProcessing) {
            track('Submit', {
                action: isManagingSubscription
                    ? 'Update Subscription Submit Finished'
                    : 'Complete Order Submit Finished',
                category: 'Checkout',
                ...common({
                    submit_type: isManagingSubscription ? 'update_subscription' : 'complete_order',
                    stage: getStage(),
                    selected_prices_count: selectedPricesCount,
                    has_selected_payment_method: !!selectedPaymentMethodId,
                    has_payment_method_error: hasPaymentMethodError,
                }),
            });
        }

        lastProcessingRef.current = isProcessing;
    }, [
        common,
        getStage,
        hasPaymentMethodError,
        isManagingSubscription,
        isProcessing,
        selectedPaymentMethodId,
        selectedPricesCount,
        track,
    ]);

    const trackEmailStarted = useCallback(() => {
        if (hasTrackedEmailStartedRef.current) return;
        hasTrackedEmailStartedRef.current = true;

        track('Input', {
            action: 'Email Started',
            category: 'Checkout',
            ...common({
                stage: 'email',
            }),
        });
    }, [common, track]);

    const trackEmailSubmitted = useCallback(() => {
        track('Submit', {
            action: 'Email Submitted',
            category: 'Checkout',
            ...common({
                stage: 'email',
            }),
        });
    }, [common, track]);

    const trackInvoiceStarted = useCallback(() => {
        if (hasTrackedInvoiceStartedRef.current) return;
        hasTrackedInvoiceStartedRef.current = true;

        track('View', {
            action: 'Invoice Started',
            category: 'Checkout',
            ...common({
                stage: 'invoice_information',
            }),
        });
    }, [common, track]);

    const trackInvoiceCompleted = useCallback(
        (meta?: { invoice_type?: 'PERSONAL' | 'COMPANY' }) => {
            track('Submit', {
                action: 'Invoice Completed',
                category: 'Checkout',
                ...common({
                    stage: 'invoice_information',
                    invoice_type: meta?.invoice_type ?? 'unknown',
                }),
            });
        },
        [common, track]
    );

    const trackCheckoutCtaClicked = useCallback(() => {
        const disabledReason = getDisabledReason();
        const stage = getStage();
        const getAction = () => {
            if (stage === 'email') {
                return 'Continue';
            } else if (stage === 'login') {
                return 'Sign in';
            } else if (isManagingSubscription) {
                return 'Update Subscription';
            } else {
                return 'Complete Order';
            }
        };
        track('Click', {
            action: getAction(),
            category: 'Checkout',
            ...common({
                stage,
                selected_prices_count: selectedPricesCount,
                has_selected_payment_method: !!selectedPaymentMethodId,
                has_saved_payment_methods: paymentMethodsCount > 0,
                payment_methods_count: paymentMethodsCount,
                is_loading_payment_methods: isLoadingPaymentMethods,
                has_payment_method_error: hasPaymentMethodError,
                disabled: !!disabledReason,
                ...(disabledReason ? { disabled_reason: disabledReason } : {}),
            }),
        });
    }, [
        common,
        getDisabledReason,
        getStage,
        hasPaymentMethodError,
        isLoadingPaymentMethods,
        isManagingSubscription,
        paymentMethodsCount,
        selectedPaymentMethodId,
        selectedPricesCount,
        track,
    ]);

    return {
        checkoutId: checkoutIdRef.current,
        trackEmailStarted,
        trackEmailSubmitted,
        trackInvoiceStarted,
        trackInvoiceCompleted,
        trackCheckoutCtaClicked,
    };
}
